import {
  QueryClient,
  MutationCache,
  matchQuery,
  QueryCache,
} from '@tanstack/react-query';

import { CustomNotification } from 'common/components';

import { customQueryFn } from 'config/axios';

const errorTracker = new Set();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      queryFn: customQueryFn,
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      const errorKey = `${query.queryKey[0]}-${JSON.stringify(query.queryKey)}`;

      if (!errorTracker.has(errorKey)) {
        //* handle tracker
        errorTracker.add(errorKey);
        setTimeout(() => {
          errorTracker.delete(errorKey);
        }, 500);

        const onError = query?.meta?.onError;

        //* onError will handle customErrorAction, and return customErrorMessage
        const errorMessage = onError ? onError(error, query) : false;
        //* if has onError but not return message => skip display globalErrorMessage
        const skip = onError && !errorMessage;

        //* Using globalErrorMessage like this may result in a raw FE error.
        //* just show a general error message 'Something went wrong.'
        const globalErrorMessage =
          typeof error === 'string' ? error : 'Something went wrong.'; // Handles cases where Promise.reject is called with a string (e.g., Promise.reject('message' || 'Server error'))

        if (skip) return;

        CustomNotification.error(errorMessage || globalErrorMessage);
      }
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (_data?.isSuccess) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              mutation?.meta?.invalidates?.some((queryKey) =>
                matchQuery({ queryKey }, query)
              ) ?? false
            );
          },
        });
      }
    },
  }),
});

export default queryClient;
